import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../core/data/redux/store";
import {
  addclassestart,
  addclassesuccess,
  addclassFailure,
  fetchclassesSuccess,
  updateclassestart,
  updateclassesuccess,
  updateclassFailure,
} from "../../../core/data/redux/classSlice";
import { addclassApi, fetchclassesApi, updateclassApi, deleteclassApi } from "../../../core/data/services/classService";
import { classes } from "../../../core/data/types/class";
import Table from "../../../core/common/dataTable/index";
import {
  classesSectionId,
  schoolClassId
} from "../../../core/common/selectoption/selectoption";
import CommonSelect, { Option } from "../../../core/common/commonSelect";
import CommonMultiSelect, { MultiOption } from "../../../core/common/commonMultiSelect";
import { TableData } from "../../../core/data/interface";
import { Link } from "react-router-dom";
import TooltipOption from "../../../core/common/tooltipOption";
import { all_routes } from "../../router/all_routes";
import { fetchTeachersApi } from "../../../core/data/services/teacherService";
import { fetchteachersSuccess } from "../../../core/data/redux/teacherSlice";
import SuccessModal from "../../../core/common/successModal";
import ErrorModal from "../../../core/common/ErrorModal";
import { getUserDetails } from "../../../lib/getUserDetails";
import { ExportColumn, exportToExcel, exportToPDF } from "../../../utils/exportUtils";

const Classes = () => {
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);

  const dispatch = useDispatch();
  const { classes: classList } = useSelector((state: RootState) => state.classes);

  const [selectedOptions, setSelectedOptions] = useState<MultiOption[]>([]);
  const [className, setClassName] = useState<Option | null>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editClassId, setEditClassId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");

  // Error modal state
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Loading state for operations
  const [operationLoading, setOperationLoading] = useState<boolean>(false);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteClassId, setDeleteClassId] = useState<number | null>(null);
  const [deleteClassName, setDeleteClassName] = useState<string>("");

  // Filter states
  const [filterClass, setFilterClass] = useState<Option | null>(null);
  const [filterSection, setFilterSection] = useState<Option | null>(null);
  const [filterStatus, setFilterStatus] = useState<Option | null>(null);
  const [filteredData, setFilteredData] = useState<classes[]>(classList);

  // Fetch data and refresh logic
  const refreshData = async () => {
    try {
      const [updatedClasses, updatedTeachers] = await Promise.all([
        fetchclassesApi(),
        fetchTeachersApi(),
      ]);
      dispatch(fetchclassesSuccess(updatedClasses));
      dispatch(fetchteachersSuccess(updatedTeachers));
      setFilteredData(updatedClasses); // Update filtered data on refresh
    } catch (error: any) {
      console.error("Error refreshing data:", error);
    }
  };

  useEffect(() => {
    let isCancelled = false;

    const fetchData = async () => {
      try {
        const [addedClasses, addedTeachers] = await Promise.all([
          fetchclassesApi(),
          fetchTeachersApi(),
        ]);

        if (!isCancelled) {
          dispatch(fetchclassesSuccess(addedClasses));
          dispatch(fetchteachersSuccess(addedTeachers));
          setFilteredData(addedClasses); // Initialize filtered data
          setIsLoading(false);
        }
      } catch (error: any) {
        if (!isCancelled) {
          console.error("Error fetching data:", error);
          const message = getErrorMessage(error);
          setErrorMessage(message);
          setShowErrorModal(true);
          setIsLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      isCancelled = true;
    };
  }, [dispatch]);

  // Update filtered data when classList or filter values change
  useEffect(() => {
    let updatedData = [...classList];

    // Filter by class
    if (filterClass) {
      updatedData = updatedData.filter((cls) => cls.name === filterClass.value);
    }

    // Filter by section
    if (filterSection) {
      updatedData = updatedData.filter((cls) =>
        cls.sectionsArr.includes(filterSection.value)
      );
    }

    // Filter by status (assuming classes have a status field)
    if (filterStatus) {
      updatedData = updatedData.filter((cls) => cls.status === filterStatus.value);
    }

    setFilteredData(updatedData);
  }, [classList, filterClass, filterSection, filterStatus]);

  const getErrorMessage = (error: any): string => {
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    if (error?.response?.data?.error) {
      return error.response.data.error;
    }
    if (error?.response?.data?.details) {
      return error.response.data.details;
    }
    if (error?.message && error.message !== "Request failed with status code 400") {
      return error.message;
    }
    return "An unexpected error occurred. Please try again.";
  };

  const handleMultiSelectChange = (selected: MultiOption[]) => {
    setSelectedOptions(selected);
  };

  const handleSelectChange = (selected: Option | null) => {
    setClassName(selected);
  };



  const handleAddClass = async (e: React.FormEvent) => {
    e.preventDefault();
    setOperationLoading(true);
    setShowErrorModal(false);
    setErrorMessage("");
    setShowSuccessModal(false);
    setSuccessMessage("");

    // Validate className and selectedOptions
    if (!className || !className.value) {
      setErrorMessage("Please select a class.");
      setShowErrorModal(true);
      setOperationLoading(false);
      return;
    }

    if (!selectedOptions || selectedOptions?.length === 0) {
      setErrorMessage("Please select at least one section.");
      setShowErrorModal(true);
      setOperationLoading(false);
      return;
    }

    const userData = getUserDetails();
    const schoolId = userData?.[0]?.relatedEntity?.schools?.id;

    const newClass: Omit<classes, "id"> = {
      name: String(className?.value),
      sections: selectedOptions.map((option) => option?.value),
      classKey: String(className?.value),
      academicYearId: 2025,
      schoolId: schoolId,
    };

    dispatch(addclassestart());
    try {
      const addedClass = await addclassApi(newClass);
      dispatch(addclassesuccess(addedClass));
      await refreshData();
      setSuccessMessage("Class added successfully!");
      setShowSuccessModal(true);
      resetForm();
    } catch (error: any) {
      console.error("Error adding class:", error);
      const message = getErrorMessage(error);
      dispatch(addclassFailure(message));
      setErrorMessage(message);
      setShowErrorModal(true);
    } finally {
      setOperationLoading(false);
    }
  };

  const handleEditClass = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editClassId) return;

    setOperationLoading(true);
    setShowErrorModal(false);
    setErrorMessage("");
    setShowSuccessModal(false);
    setSuccessMessage("");

    const originalClass = classList.find((cls: classes) => cls.id === editClassId);
    if (!originalClass) {
      setErrorMessage("Class not found for editing");
      setShowErrorModal(true);
      setOperationLoading(false);
      return;
    }

    const userData = getUserDetails();
    const schoolId = userData?.[0]?.relatedEntity?.schools?.id;

    const updatedClass: classes = {
      ...originalClass,
      id: editClassId,
      name: className ? String(className.value) : originalClass.name,
      classKey: className ? String(className.value) : originalClass.classKey,
      sections: selectedOptions.map((option) => option.value),
      academicYearId: originalClass.academicYearId || 2025,
      schoolId: schoolId,
      sectionsArr: selectedOptions.map((option) => option.value),
      sectionsIdArr: originalClass.sectionsIdArr || [],
    };

    dispatch(updateclassestart());
    try {
      const updated = await updateclassApi(updatedClass);
      dispatch(updateclassesuccess(updated));
      await refreshData();
      setSuccessMessage("Class updated successfully!");
      setShowSuccessModal(true);
      resetForm();
    } catch (error: any) {
      console.error("Error updating class:", error);
      const message = getErrorMessage(error);
      dispatch(updateclassFailure(message));
      setErrorMessage(message);
      setShowErrorModal(true);
    } finally {
      setOperationLoading(false);
    }
  };

  const handleDeleteClass = async () => {
    if (!deleteClassId) return;

    setOperationLoading(true);
    setShowErrorModal(false);
    setErrorMessage("");
    setShowSuccessModal(false);
    setSuccessMessage("");

    try {
      await deleteclassApi(deleteClassId);
      await refreshData();
      setSuccessMessage("Class deleted successfully!");
      setShowSuccessModal(true);
      setShowDeleteModal(false);
      setDeleteClassId(null);
      setDeleteClassName("");
    } catch (error: any) {
      console.error("Error deleting class:", error);
      const message = getErrorMessage(error);
      setErrorMessage(message);
      setShowErrorModal(true);
      setShowDeleteModal(false);
    } finally {
      setOperationLoading(false);
    }
  };

  const resetForm = () => {
    setClassName(null);
    setSelectedOptions([]);
    setEditMode(false);
    setEditClassId(null);
  };

  const handleEditClick = (classData: classes) => {
    setEditMode(true);
    setEditClassId(classData.id);
    setClassName({ value: classData.name, label: classData.name });
    setSelectedOptions(
      Array.isArray(classData.sectionsArr)
        ? classData.sectionsArr.map((section) => ({
          value: String(section),
          label: String(section),
        }))
        : typeof classData.sections === "string"
          ? (classData.sections as string)
            .split(",")
            .map((section: string) => ({
              value: section.trim(),
              label: section.trim(),
            }))
          : []
    );
  };

  const handleDeleteClick = (classData: classes) => {
    setDeleteClassId(classData.id);
    setDeleteClassName(classData.name);
    setShowDeleteModal(true);
  };

  const handleApplyClick = () => {
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show");
    }
  };

  const handleResetFilters = () => {
    setFilterClass(null);
    setFilterSection(null);
    setFilterStatus(null);
    setFilteredData(classList); // Reset to original data
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show");
    }
  };

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    setErrorMessage("");
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setSuccessMessage("");
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteClassId(null);
    setDeleteClassName("");
  };

  const exportColumns: ExportColumn[] = [
    { title: "Unique ID", dataIndex: "id" },
    { title: "Class", dataIndex: "name" },
    { title: "Section", dataIndex: "sectionsArr" },
  ];

  const handleExportPDF = () =>
    exportToPDF(filteredData, exportColumns, "Classes.pdf");
  const handleExportExcel = () =>
    exportToExcel(filteredData, exportColumns, "Classes.xlsx");

  const route = all_routes;
  const columns = [
    {
      title: "Unique ID",
      dataIndex: "id",
      render: (_: any, record: any) => <span>{`UNIID${ record.id }`}</span>,
    },
    {
      title: "Class",
      dataIndex: "name",
      sorter: (a: TableData, b: TableData) => {
        const aLength = a?.name?.length || 0;
        const bLength = b?.name?.length || 0;
        return aLength - bLength; // Ascending order
      },
    },
    {
      title: "Section",
      dataIndex: "sectionsArr",
      render: (sections: string[] | undefined | null) => (
        <>
          {[...(sections ?? [])]  // Fallback to empty array if null/undefined
            .sort((a, b) => a.localeCompare(b))
            .map((section) => (
              <span key={section} className="badge bg-primary me-1">
                {section}
              </span>
            ))}
        </>
      ),
    },

    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: classes) => (
        <div className="d-flex align-items-center">
          <div className="dropdown">
            <Link
              to="#"
              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ti ti-dots-vertical fs-14" />
            </Link>
            <ul className="dropdown-menu dropdown-menu-right p-3">
              <li>
                <Link
                  className="dropdown-item rounded-1"
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#add_class"
                  onClick={() => handleEditClick(record)}
                >
                  <i className="ti ti-edit-circle me-2" />
                  Edit
                </Link>
              </li>
              <li>
                <Link
                  className="dropdown-item rounded-1"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleDeleteClick(record);
                  }}
                >
                  <i className="ti ti-trash-x me-2" />
                  Delete
                </Link>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Classes List</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={route.HeadmasterDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    All Classes
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption onExportPDF={handleExportPDF} onExportExcel={handleExportExcel} />
              <div className="mb-2">
                <Link
                  to="#"
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#add_class"
                >
                  <i className="ti ti-square-rounded-plus-filled me-2" />
                  Add Class
                </Link>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Classes List</h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-filter me-2" />
                    Filter
                  </Link>
                  <div className="dropdown-menu drop-width" ref={dropdownMenuRef}>
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 border-bottom pb-0">
                        <div className="row">
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">Class</label>
                              <CommonSelect
                                className="select"
                                options={schoolClassId}
                                defaultValue={filterClass}
                                onChange={(selected: Option | null) => setFilterClass(selected)}
                              />
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">Section</label>
                              <CommonSelect
                                className="select"
                                options={classesSectionId}
                                defaultValue={filterSection}
                                onChange={(selected: Option | null) => setFilterSection(selected)}
                              />
                            </div>
                          </div>
                          {/* <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">Status</label>
                              <CommonSelect
                                className="select"
                                options={activeList}
                                defaultValue={filterStatus}
                                onChange={(selected: Option | null) => setFilterStatus(selected)}
                              />
                            </div>
                          </div> */}
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <Link to="#" className="btn btn-light me-3" onClick={handleResetFilters}>
                          Reset
                        </Link>
                        <Link to="#" className="btn btn-primary" onClick={handleApplyClick}>
                          Apply
                        </Link>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body p-0 py-3">
              <Table columns={columns} dataSource={filteredData} Selection={true} />
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade" id="add_class">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">{editMode ? "Edit Class" : "Add Class"}</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={resetForm}
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={editMode ? handleEditClass : handleAddClass}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Class Name</label>
                      <CommonSelect
                        className="select"
                        options={schoolClassId}
                        defaultValue={className}
                        onChange={handleSelectChange}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Section</label>
                      <CommonMultiSelect
                        options={classesSectionId.map(option => ({
                          ...option,
                          value: String(option.value)
                        }))}
                        value={selectedOptions}
                        onChange={handleMultiSelectChange}
                        isLoading={isLoading}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <Link
                  to="#"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={resetForm}
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  className="btn btn-primary"
                  data-bs-dismiss="modal"
                  disabled={operationLoading}
                >
                  {operationLoading
                    ? editMode
                      ? "Saving..."
                      : "Adding..."
                    : editMode
                      ? "Save Changes"
                      : "Add Class"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Delete Class</h4>
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  onClick={handleCloseDeleteModal}
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <div className="modal-body">
                <div className="text-center">
                  <div className="mb-3">
                    <i className="ti ti-trash-x text-danger" style={{ fontSize: '48px' }}></i>
                  </div>
                  <h5 className="mb-3">Are you sure?</h5>
                  <p className="mb-3">
                    Do you want to delete the class <strong>"{deleteClassName}"</strong>?
                    This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="modal-footer justify-content-center">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  onClick={handleCloseDeleteModal}
                  disabled={operationLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteClass}
                  disabled={operationLoading}
                >
                  {operationLoading ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <SuccessModal
        show={showSuccessModal}
        onClose={handleCloseSuccessModal}
        message={successMessage}
      />

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={handleCloseErrorModal}
      />
    </div>
  );
};

export default Classes;